'use client';

import {
  Badge,
  cn,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import type { ToolUIPart } from 'ai';
import { CheckCircleIcon, CircleIcon, ClockIcon, CodeIcon, ShieldCheckIcon, XCircleIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

export type SandboxProps = ComponentProps<'div'>;

export function Sandbox({ className, ...props }: SandboxProps) {
  return (
    <div
      className={cn('not-prose bg-muted/15 w-full overflow-hidden rounded-md border', className)}
      data-testid="sandbox"
      {...props}
    />
  );
}

export type SandboxHeaderProps = ComponentProps<'div'> & {
  title?: string;
  state: ToolUIPart['state'];
};

const sandboxStatusLabels: Record<ToolUIPart['state'], string> = {
  'input-streaming': 'Pending',
  'input-available': 'Running',
  'approval-requested': 'Approval Required',
  'approval-responded': 'Running',
  'output-available': 'Completed',
  'output-error': 'Error',
  'output-denied': 'Denied',
};

const sandboxStatusIcons: Record<ToolUIPart['state'], ReactNode> = {
  'input-streaming': <CircleIcon className="size-3" />,
  'input-available': <ClockIcon className="size-3 animate-pulse" />,
  'approval-requested': <ClockIcon className="size-3 animate-pulse" />,
  'approval-responded': <ClockIcon className="size-3 animate-pulse" />,
  'output-available': <CheckCircleIcon className="text-grass-500 dark:text-grass-300 size-3" />,
  'output-error': <XCircleIcon className="text-destructive size-3" />,
  'output-denied': <XCircleIcon className="text-destructive size-3" />,
};

function SandboxStatusBadge({ state }: { state: ToolUIPart['state'] }) {
  return (
    <Badge className="gap-1 rounded-full px-1.5 py-0 text-[11px] leading-4 font-normal" variant="secondary">
      {sandboxStatusIcons[state]}
      {sandboxStatusLabels[state]}
    </Badge>
  );
}

const sandboxSecurityMessage =
  'Nova runs these commands in an isolated cloud environment, separate from your device and Newfront systems.';

function SandboxSecurityTooltip() {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label="About the shell environment"
            className="text-muted-foreground hover:text-foreground bg-muted/60 border-border/70 inline-flex size-6 items-center justify-center rounded-md border transition-colors"
            type="button"
          >
            <ShieldCheckIcon className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-72 text-xs leading-5">
          <p>{sandboxSecurityMessage}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function SandboxHeader({ className, title, state, ...props }: SandboxHeaderProps) {
  return (
    <div
      className={cn('flex w-full items-center justify-between gap-3 px-2.5 py-1.5', className)}
      data-testid="sandbox-header"
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2">
        <CodeIcon className="text-muted-foreground size-3.5 shrink-0" />
        <span className="truncate text-xs font-medium">{title}</span>
        <SandboxStatusBadge state={state} />
      </div>
      <SandboxSecurityTooltip />
    </div>
  );
}

export type SandboxContentProps = ComponentProps<'div'>;

export function SandboxContent({ className, ...props }: SandboxContentProps) {
  return <div className={cn('outline-none', className)} data-testid="sandbox-content" {...props} />;
}

export type SandboxTabsProps = ComponentProps<typeof Tabs>;

export function SandboxTabs({ className, ...props }: SandboxTabsProps) {
  return <Tabs className={cn('w-full gap-0', className)} {...props} />;
}

export type SandboxTabsBarProps = ComponentProps<'div'>;

export function SandboxTabsBar({ className, ...props }: SandboxTabsBarProps) {
  return <div className={cn('border-border/70 flex w-full items-center border-y', className)} {...props} />;
}

export type SandboxTabsListProps = ComponentProps<typeof TabsList>;

export function SandboxTabsList({ className, ...props }: SandboxTabsListProps) {
  return (
    <TabsList
      className={cn('h-auto rounded-none border-0 bg-transparent p-0', className)}
      data-testid="sandbox-tabs-list"
      {...props}
    />
  );
}

export type SandboxTabsTriggerProps = ComponentProps<typeof TabsTrigger>;

export function SandboxTabsTrigger({ className, ...props }: SandboxTabsTriggerProps) {
  return (
    <TabsTrigger
      className={cn(
        'text-muted-foreground data-[state=active]:text-foreground data-[state=active]:border-primary',
        'rounded-none border-0 border-b-2 border-transparent px-3 py-1.5 text-xs font-medium transition-colors',
        'data-[state=active]:bg-transparent data-[state=active]:shadow-none',
        className,
      )}
      {...props}
    />
  );
}

export type SandboxTabContentProps = ComponentProps<typeof TabsContent>;

export function SandboxTabContent({ className, ...props }: SandboxTabContentProps) {
  return <TabsContent className={cn('mt-0 text-xs', className)} {...props} />;
}
